import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * OAK-Pad context — single source of truth for clipped snippets.
 *
 * Storage strategy:
 *   - Anonymous users: localStorage only (persists across sessions on
 *     this device).
 *   - Authenticated users: server is the source of truth on hydration.
 *     Local writes still hit localStorage immediately (instant UX) AND
 *     fire-and-forget POST/DELETE to /api/oakpad/clips so a clip from
 *     phone shows up on laptop.
 *   - On sign-in: any local-only clips that the server doesn't know
 *     about get pushed up (one-time migration) so users don't lose
 *     pre-login clips.
 */
const STORAGE_KEY = "oak_pad_snippets_v1";
const MAX_SNIPPETS = 200;

function _readSnippets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function _writeSnippets(arr) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(0, MAX_SNIPPETS)));
  } catch {
    // quota exceeded — best-effort drop
  }
}

const OakPadContext = createContext(null);

export function OakPadProvider({ children }) {
  const { user } = useAuth();
  const [snippets, setSnippets] = useState(() => _readSnippets());
  const [open, setOpen] = useState(false);
  // Per-tab lock so a hydration-race during fast nav doesn't double-fire
  // the migration push or overwrite local edits with stale server data.
  const hydratedFor = useRef(null);

  useEffect(() => {
    _writeSnippets(snippets);
  }, [snippets]);

  // Sync on user change. Logout clears the in-memory list (but not local
  // storage — anonymous browsing on the same device still works).
  useEffect(() => {
    if (!user) {
      hydratedFor.current = null;
      return;
    }
    if (hydratedFor.current === user.user_id) return;
    hydratedFor.current = user.user_id;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/oakpad/clips");
        const serverClips = (data?.clips || []).map((c) => ({
          id: c.clip_id,
          text: c.text,
          source_path_id: c.source_path_id || null,
          source_step_index: c.source_step_index ?? null,
          source_title: c.source_title || null,
          created_at: c.created_at,
        }));

        // Merge: server is source of truth for IDs the server knows about.
        // Local-only clips (created before sign-in or while offline) get
        // pushed up so the multi-device promise holds.
        const serverIds = new Set(serverClips.map((c) => c.id));
        const localOnly = _readSnippets().filter((c) => !serverIds.has(c.id));
        if (localOnly.length > 0) {
          await Promise.allSettled(
            localOnly.map((c) =>
              api.post("/oakpad/clips", {
                clip_id: c.id,
                text: c.text,
                source_path_id: c.source_path_id || null,
                source_step_index: c.source_step_index ?? null,
                source_title: c.source_title || null,
                created_at: c.created_at,
              })
            )
          );
        }
        if (cancelled) return;
        // Re-merge: server clips take precedence, then any local-only
        // we just pushed (so the user sees them immediately even if a
        // push call is still in flight).
        const merged = [
          ...serverClips,
          ...localOnly.filter((c) => !serverClips.some((s) => s.id === c.id)),
        ].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
        setSnippets(merged.slice(0, MAX_SNIPPETS));
      } catch {
        // Backend unreachable / 401 — keep local-only state. The user
        // can still clip; sync resumes on next successful auth call.
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const addSnippet = useCallback((text, source = {}) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return null;
    const snip = {
      id: `clip_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      text: trimmed.slice(0, 4000),
      source_path_id: source.path_id || null,
      source_step_index: typeof source.step_index === "number" ? source.step_index : null,
      source_title: source.title || null,
      created_at: new Date().toISOString(),
    };
    setSnippets((prev) => [snip, ...prev].slice(0, MAX_SNIPPETS));
    // Authed users — push to server. Don't await; UI is already updated.
    if (user?.user_id) {
      api.post("/oakpad/clips", {
        clip_id: snip.id,
        text: snip.text,
        source_path_id: snip.source_path_id,
        source_step_index: snip.source_step_index,
        source_title: snip.source_title,
        created_at: snip.created_at,
      }).catch(() => {});
    }
    return snip;
  }, [user]);

  const removeSnippet = useCallback((id) => {
    setSnippets((prev) => prev.filter((s) => s.id !== id));
    if (user?.user_id) {
      api.delete(`/oakpad/clips/${encodeURIComponent(id)}`).catch(() => {});
    }
  }, [user]);

  const clearAll = useCallback(() => {
    setSnippets([]);
    if (user?.user_id) {
      api.delete("/oakpad/clips").catch(() => {});
    }
  }, [user]);

  const openPad = useCallback(() => setOpen(true), []);
  const closePad = useCallback(() => setOpen(false), []);

  return (
    <OakPadContext.Provider
      value={{ snippets, addSnippet, removeSnippet, clearAll, open, openPad, closePad, synced: Boolean(user) }}
    >
      {children}
    </OakPadContext.Provider>
  );
}

export function useOakPad() {
  const ctx = useContext(OakPadContext);
  if (!ctx) throw new Error("useOakPad must be used inside OakPadProvider");
  return ctx;
}
